import { createClasses, getDirection } from "@bloomberg/media-ui-core";
import Link from "next/link";
import React from "react";
import {
  Change,
  QuickAddContainer,
  Variant,
} from "@bloomberg/media-ui-markets";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useFeatureGate } from "@foundation/statsig";
import { ChevronRight } from "@bloomberg/media-ui-icons";
import { Placeholder } from "@bloomberg/media-ui-article";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import { SectionFrontColumn, SectionFrontSecurity } from "~types/sectionfront";
import styles from "~components/SectionFront/style.module.css";
import {
  formatChange,
  formatPercentChange,
  formatValue,
} from "~utils/section-front/formatChange";
import { countryTicker2Link } from "~utils/section-front/country";
import { useLocale } from "~hooks/i18n/useLocale";
import type { TableColumnDefinition, TableColumnRenderer } from "~types/table";
import { Locale } from "~constants/i18n";

const PlaceholderCell = React.memo(
  (props: { width?: string; height?: string }) => {
    const { width = "80px", height = "16px" } = props;

    return (
      <div data-sectionFront-cellState="loading" style={{ width, height }}>
        <Placeholder.Line />
      </div>
    );
  }
);

PlaceholderCell.displayName = "PlaceholderCell";

function getValue<T>(
  security: SectionFrontSecurity,
  key: keyof SectionFrontSecurity,
  defaultValue?: T
): T {
  return (security[key] ?? defaultValue) as T;
}

function getColumnPrecision(
  column: TableColumnDefinition<SectionFrontSecurity>,
  defaultValue: number
) {
  const precision = column.options?.precision;

  if (typeof precision === "number" && precision >= 0) {
    return column.options?.precision as number;
  }

  return defaultValue;
}

const renderName = (security: SectionFrontSecurity) => {
  return (
    <span
      className={createClasses([
        styles.stockName,
        typographyStyles.coreShortTextMd,
      ])}
    >
      {security.name}
    </span>
  );
};

const renderCrossRateName = (security: SectionFrontSecurity) => {
  return (
    <span className={createClasses([styles.stockName, styles.crossRate])}>
      {security.name}
    </span>
  );
};

const trackQuickAdd = (eventAction: string, eventLabel: string) => {
  trackClickEvent("Watchlist", eventLabel, eventAction, "Watchlist");
};

const RenderCurrencyDisplayID = (security: SectionFrontSecurity) => {
  const featureGateEnabled = Boolean(
    useFeatureGate("watchlist_paywall")?.value ?? false
  );
  const { id } = security;
  const stockUrl = `/quote/${id}`;

  const [currency] = id.split(":");
  const displayName = `${currency.slice(0, 3)}-${currency.slice(3, 6)}`;

  const { isJapanese } = useLocale();

  return (
    <div className={styles.currencyIdContainer}>
      {!isJapanese && (
        <QuickAddContainer
          ticker={id}
          variant="icon"
          className={styles.quickAdd}
          trackClick={trackQuickAdd}
          disabled={!featureGateEnabled}
        />
      )}
      <Link className={styles.displayId} href={stockUrl} target="_blank">
        <div className={styles.stockName}>{displayName}</div>
      </Link>
    </div>
  );
};

const REGION_ECONOMIES_VARIANT = "Economies";

const DisplayID: React.FC<{
  security: SectionFrontSecurity;
  column?: TableColumnDefinition<SectionFrontSecurity>;
}> = ({ security, column }) => {
  const featureGateEnabled = Boolean(
    useFeatureGate("watchlist_paywall")?.value ?? false
  );

  const { id, name, longName } = security;
  const stockUrl = `/quote/${id}`;

  const { isJapanese } = useLocale();

  return (
    <div className={styles.displayIdContainer}>
      {!isJapanese && (
        <QuickAddContainer
          ticker={id}
          variant="icon"
          className={styles.quickAdd}
          trackClick={trackQuickAdd}
          disabled={!featureGateEnabled}
        />
      )}
      <Link className={styles.displayId} href={stockUrl} target="_blank">
        <div className={styles.stockId}>{id}</div>
        <div
          className={createClasses([
            styles.stockName,
            {
              [styles.regionEconomiesName]: column?.options?.showFullTickerName,
            },
          ])}
        >
          {column?.options?.showFullTickerName ? longName : name}
        </div>
      </Link>
    </div>
  );
};

const SecurityNameCell: React.FC<{ security: SectionFrontSecurity }> = ({
  security,
}) => {
  const featureGateEnabled = Boolean(
    useFeatureGate("watchlist_paywall")?.value ?? false
  );

  const { id, securityName, longName } = security;
  const stockUrl = `/quote/${id}`;
  const securityDisplayName = longName || securityName;

  const { isJapanese } = useLocale();

  return (
    <div className={styles.displayIdContainer}>
      {!isJapanese && (
        <QuickAddContainer
          ticker={id}
          variant="icon"
          className={styles.quickAdd}
          disabled={!featureGateEnabled}
        />
      )}
      <Link className={styles.displayId} href={stockUrl} target="_blank">
        <div className={styles.stockId}>{id}</div>
        <div className={styles.stockName} title={securityDisplayName}>
          {securityDisplayName}
        </div>
      </Link>
    </div>
  );
};

const renderInterestRateName = (security: SectionFrontSecurity) => {
  const { securityName, thirdPartyUrl } = security;

  if (thirdPartyUrl) {
    return (
      <Link className={styles.displayId} href={thirdPartyUrl}>
        <div
          className={createClasses([
            styles.stockName,
            styles.entityName,
            styles.consumerInterestRate,
          ])}
        >
          {securityName}
        </div>
      </Link>
    );
  }

  return (
    <span
      className={createClasses([
        styles.stockName,
        styles.entityName,
        styles.consumerInterestRate,
      ])}
    >
      {securityName}
    </span>
  );
};

const renderCountry = (security: SectionFrontSecurity) => {
  const { id, countryName } = security;
  const link = countryTicker2Link(id);

  if (link) {
    return (
      <Link className={styles.displayId} href={link}>
        <div className={createClasses([styles.stockName, styles.entityName])}>
          <span>{countryName}</span>
          <ChevronRight />
        </div>
      </Link>
    );
  }

  return (
    <span className={createClasses([styles.stockName, styles.entityName])}>
      {countryName}
    </span>
  );
};

const renderNumberValue = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const precision = getColumnPrecision(column, 2);
  const columnKey =
    (column.options?.backupKey as keyof SectionFrontSecurity) || column.key;
  const rawValue = getValue<number>(security, columnKey);
  const value = formatValue(rawValue, precision);

  return <span className={styles.stockValue}>{value}</span>;
};

const CONTRACT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
});

const JA_CONTRACT_DATE_FORMATTER = new Intl.DateTimeFormat("ja-JP", {
  year: "numeric",
  month: "short",
});

const renderContractDateLocale = (security: SectionFrontSecurity) => {
  const { locale, commodityContractDate } = security;

  if (!commodityContractDate) {
    return <span>N/A</span>;
  }
  const date = new Date(commodityContractDate as string);

  const dateLocale =
    locale === Locale.Ja
      ? JA_CONTRACT_DATE_FORMATTER.format(date)
      : CONTRACT_DATE_FORMATTER.format(date);

  return <span suppressHydrationWarning={true}> {dateLocale} </span>;
};

const renderPriceChange = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const rawValue = getValue<number>(security, column.key);

  return (
    <Change
      percentChange={rawValue}
      priceChange={rawValue}
      hidePercentChange
      variant={Variant.refresh2024}
    />
  );
};

const renderPercentChange = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const rawValue = getValue<number>(security, column.key);

  return (
    <Change
      percentChange={rawValue}
      className="sectionFront"
      variant={Variant.refresh2024}
    />
  );
};

const renderYield = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const precision = getColumnPrecision(column, 2);
  const rawValue = getValue<number>(security, column.key);
  const value = formatPercentChange(rawValue, precision);

  return <span className={styles.stockValue}>{value}</span>;
};

const renderYieldChange = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const precision = getColumnPrecision(column, 0);

  const columnKey =
    (column.options?.backupKey as keyof SectionFrontSecurity) || column.key;
  const yieldChange = getValue<number>(security, columnKey);

  const rawValue = yieldChange
    ? yieldChange * ((column.options?.multiplier || 1) as number)
    : yieldChange;
  const value = formatChange(rawValue, precision);

  // we invert the raw value to get the correct direction arrow,
  // to follow the logic for bond yield changes, where yields
  // going up (bad) are red, and yields going down (good) are green
  // so if yield change is +0.1, we show +0.1 with a down arrow
  // if yield change is -0.1, we show -0.1 with an up arrow
  // if yield change is 0, we show 0 with no arrow
  const direction = getDirection(-rawValue);

  return (
    <span
      className={createClasses([
        styles.yieldValue,
        {
          [styles.positive]: direction === "positive",
          [styles.negative]: direction === "negative",
        },
      ])}
    >
      {value}
    </span>
  );
};

const renderTime = (security: SectionFrontSecurity) => {
  return (
    <span suppressHydrationWarning={true} className={styles.stockTime}>
      {security.lastUpdatedTime || "--"}
    </span>
  );
};

const renderYieldPercentage = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const precision = getColumnPrecision(column, 2);
  const columnKey =
    (column.options?.backupKey as keyof SectionFrontSecurity) || column.key;
  const rawValue = getValue<number>(security, columnKey);
  const value = formatValue(rawValue, precision);

  return <span className={styles.stockValue}>{value}%</span>;
};

const renderLoadingNumberValue = (
  security: SectionFrontSecurity,
  column: TableColumnDefinition<SectionFrontSecurity>
) => {
  const precision = getColumnPrecision(column, 2);
  const columnKey =
    (column.options?.backupKey as keyof SectionFrontSecurity) || column.key;
  const rawValue = getValue<number>(security, columnKey);
  const value = formatValue(rawValue, precision) || "--";

  return (
    <>
      <span data-sectionFront-cellState="success" className={styles.stockValue}>
        {value}
      </span>
      <PlaceholderCell />
    </>
  );
};

const renderer: { [key: string]: TableColumnRenderer<SectionFrontSecurity> } = {
  country: renderCountry,
  coupon: renderNumberValue,
  commodityContractDate: (s) => renderContractDateLocale(s),
  crossRate: renderNumberValue,
  crossRateName: renderCrossRateName,
  currency: RenderCurrencyDisplayID,
  highPrice: renderNumberValue,
  id: (security, column) => <DisplayID security={security} column={column} />,
  interestRate: renderInterestRateName,
  interestRatePrice: renderYieldPercentage,
  interestRatePrice1MonthAgo: renderYieldPercentage,
  lastUpdatedTime: renderTime,
  lowPrice: renderNumberValue,
  municipalYield: renderYieldPercentage,
  municipalYieldChange1Day: renderYieldChange,
  municipalYieldChange1Month: renderYieldChange,
  municipalYieldChange1Year: renderYieldChange,
  name: renderName,
  percentChange1Day: renderPercentChange,
  price: renderNumberValue,
  priceChange1Day: renderPriceChange,
  percentChange1Month: renderPriceChange,
  percentChange1Year: renderPercentChange,
  rate: renderLoadingNumberValue,
  ratePrice: renderNumberValue,
  ratePrice1YearAgo: renderNumberValue,
  securityName: (s) => <SecurityNameCell security={s} />,
  yield: renderYield,
  yieldChange1Day: renderYieldChange,
  yieldChange1Month: renderYieldChange,
  yieldChange1Year: renderYieldChange,
};

export const getTableColumns = (
  sectionColumns: Array<SectionFrontColumn>,
  isJapanese: boolean
): Array<TableColumnDefinition<SectionFrontSecurity>> => {
  return sectionColumns.map((column) => {
    const tableColumn: TableColumnDefinition<SectionFrontSecurity> = {
      title: column.title,
      key: column.key as keyof SectionFrontSecurity,
      align: column.align,
      renderer: renderer[column.key],
      options: {
        ...column.options,
        disableQuickAdd: column.options?.disableQuickAdd || Boolean(isJapanese),
      },
      allowSorting: column.options?.disableSorting !== true,
    };

    return tableColumn;
  });
};
